import { Link } from "react-router-dom";
import { useEffect } from "react";
import Logo from "../components/Logo";
import {
  ArrowRight, CheckCircle, Sparkle, Timer, CurrencyDollar,
  Truck, ShieldCheck, MapPin, PlayCircle, MusicNote,
} from "@phosphor-icons/react";
import { trackConversion } from "../lib/analytics";
import HomeScreenPrompt from "../components/HomeScreenPrompt";
import { useExperiments } from "../lib/experiments";

/*
  TikTokLandingPage — dedicated funnel for TikTok short-form ad + organic traffic.

  Route: /tiktok

  Design rules for max conversion on TikTok cold traffic:
    - MOBILE-FIRST. TikTok is 95%+ mobile — assume 390px viewport by default.
    - Vertical layout, single-column, thumb-scrollable. Zero side-by-side grids.
    - ONE CTA above the fold. No nav bar, no dropdowns.
    - Copy mirrors TikTok trucker-culture language ("stop leaving money on the load",
      "your dispatcher texts you all day", etc.) — not corporate SaaS-speak.
    - Numbers-first proof (dollars saved, hours reclaimed).
    - Sticky bottom CTA follows the thumb.
    - Auto-tags every outbound CTA with utm_source=tiktok so signups land in the
      right analytics bucket without the ad tech team having to set anything up.
    - Fires a landing_view pixel event on mount for retargeting.
*/

const TIKTOK_UTM = "utm_source=tiktok&utm_medium=short_form&utm_campaign=tiktok_ad";
const PILOT_CTA = `/signup?${TIKTOK_UTM}`;
const FREE_WORKFLOW_CTA = `/contact?intent=pilot&${TIKTOK_UTM}`;

const PROOF = [
  { icon: CurrencyDollar, value: "+$2,400/mo", label: "recovered in underpriced loads" },
  { icon: Timer, value: "12+ hrs/wk", label: "given back to owner-operators" },
  { icon: Truck, value: "1–50 trucks", label: "the sweet-spot fleet size we serve" },
];

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "A broker offers you a load.",
    body: "Run BrokerCheck first — authority, operating status and the FMCSA record in seconds. Then AI drafts your counter-offer; you decide what to send.",
  },
  {
    n: "02",
    title: "Send your packet. Get a text when it opens.",
    body: "Your carrier packet goes out in one tap. The moment the broker opens it, you get a text — call while you're still on their screen.",
  },
  {
    n: "03",
    title: "Driver hauls. Payroll and IFTA miles roll up.",
    body: "Driver pay per mile, percentage or salary, pay stubs, and a state-mileage breakdown every quarter — no spreadsheets.",
  },
];

const FAQS = [
  {
    q: "Is this just another dispatcher app?",
    a: "No. Dispatcher apps make you type more. FreightPilot handles the paperwork around the load — broker verification, carrier packets with open alerts, MC and insurance tracking, driver pay and IFTA miles. AI drafts your replies and counter-offers; you approve.",
  },
  {
    q: "What does it cost?",
    a: "7-day free trial — card on file, $0 today. Then $79/mo Solo up to $299/mo Small Fleet ($499 Growth). Cancel anytime before day 8 and you pay nothing.",
  },
  {
    q: "How fast can I start?",
    a: "Same day. Sign up, add a card, and you're in. Want a hand? Book a 15-min setup call and we'll configure it with you.",
  },
];

export default function TikTokLandingPage() {
  const { getSlot } = useExperiments("/tiktok");

  useEffect(() => {
    document.title = "TikTok Trucker Special · 7-Day Free Trial | FreightPilot AI";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Saw us on TikTok? Broker checks, carrier packets with open alerts, driver payroll and IFTA miles — from your phone. 7-day free trial, cancel anytime."
      );
    }
    trackConversion("landing_view", { landing_page: "tiktok" });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="tiktok-landing-page">
      <MinimalHeader />
      <Hero getSlot={getSlot} />
      <TrustStrip />
      <ProofGrid />
      <HowItWorks />
      <PilotBanner />
      <FAQ />
      <FinalCTA />
      <MinimalFooter />
      <StickyCTA />
      <HomeScreenPrompt source="tiktok" />
    </div>
  );
}

/* ─────────────────────  HEADER (no nav, single tiny logo)  ───────────────────── */

function MinimalHeader() {
  return (
    <header
      className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10"
      data-testid="tiktok-header"
    >
      <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" data-testid="tiktok-logo-home">
          <Logo />
        </Link>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold">
          <MusicNote size={11} weight="fill" /> Seen on TikTok
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────  HERO  ───────────────────── */

function Hero({ getSlot }) {
  // A/B — hero hook copy is served from the /tiktok experiment. Falls back
  // to the pain-hook control string when the experiment isn't running.
  const heroHook = getSlot("hero_hook", "Stop leaving $80–150 on every load.");
  return (
    <section
      id="hero"
      data-testid="tiktok-hero"
      className="relative pt-10 pb-14 sm:pt-14 sm:pb-20 overflow-hidden"
    >
      {/* Soft orange glow */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          background:
            "radial-gradient(60% 45% at 50% 15%, rgba(255,90,31,0.14) 0%, rgba(255,90,31,0) 70%)",
        }}
      />
      <div className="max-w-2xl mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6">
          <Sparkle size={12} weight="fill" /> Trucker software · 7-day free trial
        </div>

        <h1
          data-testid="tiktok-hero-headline"
          className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.05] tracking-tight mb-5"
        >
          {heroHook}
        </h1>
        <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mb-8 max-w-lg mx-auto">
          Check the broker, send your packet, get a text when it opens, run driver pay
          and IFTA miles — from your phone. 7-day free trial, cancel anytime.
        </p>

        <div className="flex flex-col items-stretch gap-3 max-w-sm mx-auto">
          <Link
            to={PILOT_CTA}
            onClick={() => trackConversion("tiktok_hero_cta")}
            data-testid="tiktok-hero-primary-cta"
            className="btn-primary rounded-full px-6 py-4 text-base font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-[#FF5A1F]/25"
          >
            Start 7-day free trial
            <ArrowRight size={16} weight="bold" />
          </Link>
          <a
            href="#how-it-works"
            data-testid="tiktok-hero-secondary-cta"
            className="rounded-full px-6 py-3 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center justify-center gap-2 text-neutral-200"
          >
            <PlayCircle size={16} weight="fill" className="text-[#FF9764]" />
            How it works (30 sec)
          </a>
        </div>

        <p className="mt-5 text-xs text-neutral-500">
          Card on file · $0 today · cancel anytime before day 8
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────  TRUST STRIP  ───────────────────── */

function TrustStrip() {
  return (
    <section
      aria-label="Trust signals"
      data-testid="tiktok-trust-strip"
      className="border-y border-white/10 bg-black/40"
    >
      <div className="max-w-2xl mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-widest text-neutral-500">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={13} weight="fill" className="text-emerald-400" />
          FMCSA-verified carriers only
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MapPin size={13} weight="fill" className="text-[#FF5A1F]" />
          Built in Sioux Falls, SD
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Truck size={13} weight="fill" className="text-[#FF9764]" />
          For 1–50 truck fleets
        </span>
      </div>
    </section>
  );
}

/* ─────────────────────  PROOF GRID  ───────────────────── */

function ProofGrid() {
  return (
    <section
      aria-label="Real pilot results"
      data-testid="tiktok-proof-grid"
      className="py-14 border-b border-white/10"
    >
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="overline mb-2 text-[#FF9764]">What carriers see</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-snug">
            Real numbers from real trucks.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PROOF.map((p, i) => (
            <div
              key={p.label}
              data-testid={`tiktok-proof-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-5 text-center"
            >
              <p.icon size={22} weight="fill" className="text-[#FF5A1F] mx-auto mb-2.5" />
              <div className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight leading-none mb-1.5">
                {p.value}
              </div>
              <div className="text-xs text-neutral-400 leading-snug">{p.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  HOW IT WORKS  ───────────────────── */

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="tiktok-how-it-works"
      className="py-14 border-b border-white/10 scroll-mt-16"
    >
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="overline mb-2 text-[#FF9764]">How it works</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-snug">
            Three moments. Zero paperwork.
          </h2>
        </div>
        <div className="space-y-3">
          {HOW_IT_WORKS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`tiktok-step-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 relative overflow-hidden"
            >
              <div className="font-mono text-5xl sm:text-6xl font-semibold text-[#FF5A1F]/15 leading-none absolute -top-1 right-3 select-none pointer-events-none">
                {s.n}
              </div>
              <div className="relative">
                <h3 className="font-display text-lg sm:text-xl font-semibold text-white leading-snug mb-1.5">
                  {s.title}
                </h3>
                <p className="text-neutral-400 text-sm leading-relaxed pr-10">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  PILOT BANNER  ───────────────────── */

function PilotBanner() {
  return (
    <section
      aria-label="Free trial"
      data-testid="tiktok-pilot-banner"
      className="py-14 border-b border-white/10 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(70% 50% at 50% 50%, rgba(255,90,31,0.12) 0%, rgba(255,90,31,0) 70%)",
        }}
      />
      <div className="max-w-2xl mx-auto px-4">
        <div className="rounded-2xl border border-[#FF5A1F]/30 bg-gradient-to-br from-[#FF5A1F]/[0.08] via-black/40 to-black/20 p-6 sm:p-8 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF5A1F] text-black text-[10px] uppercase tracking-widest font-bold mb-4">
            <Sparkle size={11} weight="fill" /> 7-day free trial
          </div>
          <h3 className="font-display text-2xl sm:text-[28px] font-semibold tracking-tight leading-snug mb-3">
            Try everything.<br className="sm:hidden" />
            <span className="text-[#FF5A1F]"> Pay nothing today.</span>
          </h3>
          <ul className="text-sm text-neutral-200 space-y-2 max-w-xs mx-auto mb-6 text-left">
            {[
              "Full access for 7 days",
              "Card on file · $0 charged today",
              "Cancel anytime before day 8 — no charge",
              "Then monthly, cancel anytime",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2 leading-snug">
                <CheckCircle size={16} weight="fill" className="text-[#FF5A1F] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to={PILOT_CTA}
            onClick={() => trackConversion("tiktok_pilot_banner_cta")}
            data-testid="tiktok-pilot-banner-cta"
            className="btn-primary rounded-full px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2"
          >
            Start my 7-day trial
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  FAQ  ───────────────────── */

function FAQ() {
  return (
    <section
      aria-label="Frequently asked"
      data-testid="tiktok-faq"
      className="py-14 border-b border-white/10"
    >
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="overline mb-2 text-[#FF9764]">FAQ</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-snug">
            Questions we get in the comments.
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details
              key={f.q}
              data-testid={`tiktok-faq-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 group open:border-white/25"
            >
              <summary className="cursor-pointer list-none font-medium text-white text-sm sm:text-base flex items-center justify-between gap-3">
                {f.q}
                <span className="text-[#FF9764] text-lg leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-neutral-400 text-sm leading-relaxed mt-3 pt-3 border-t border-white/5">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  FINAL CTA  ───────────────────── */

function FinalCTA() {
  return (
    <section
      data-testid="tiktok-final-cta"
      className="py-16 sm:py-20 text-center"
    >
      <div className="max-w-2xl mx-auto px-4">
        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug mb-4">
          Ready to <span className="text-[#FF5A1F]">get your Sundays back</span>?
        </h2>
        <p className="text-neutral-400 text-sm sm:text-base leading-relaxed mb-7 max-w-md mx-auto">
          Start the 7-day trial. If you want, book a 15-min setup call and Michael
          will configure your first workflow with you.
        </p>
        <div className="flex flex-col items-stretch gap-3 max-w-sm mx-auto">
          <Link
            to={PILOT_CTA}
            onClick={() => trackConversion("tiktok_final_cta")}
            data-testid="tiktok-final-cta-primary"
            className="btn-primary rounded-full px-6 py-4 text-base font-semibold inline-flex items-center justify-center gap-2"
          >
            Start 7-day free trial
            <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to={FREE_WORKFLOW_CTA}
            data-testid="tiktok-final-cta-secondary"
            className="rounded-full px-6 py-3 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center justify-center gap-2 text-neutral-200"
          >
            Or book a 15-min setup call
            <ArrowRight size={14} />
          </Link>
          <Link
            to="/authority-checklist"
            onClick={() => trackConversion("tiktok_authority_checklist")}
            data-testid="tiktok-authority-checklist-link"
            className="text-sm text-neutral-400 hover:text-white transition-colors inline-flex items-center justify-center gap-1.5 pt-1"
          >
            Just applied for your MC? Grab the free New Authority Checklist
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  FOOTER (minimal)  ───────────────────── */

function MinimalFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="tiktok-footer"
      className="border-t border-white/10 py-8 bg-black/40"
    >
      <div className="max-w-2xl mx-auto px-4 text-center text-xs text-neutral-500 space-y-2">
        <div className="inline-flex items-center gap-1.5 text-neutral-400">
          <MapPin size={12} weight="fill" className="text-[#FF5A1F]" />
          Built in Sioux Falls, SD · USA
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <a href="mailto:contact@freightpilotai.ai" className="hover:text-white">
            contact@freightpilotai.ai
          </a>
          <a href="tel:+16056797307" className="hover:text-white tabular-nums">
            (605) 679-7307
          </a>
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/terms" className="hover:text-white">Terms</Link>
        </div>
        <div>© {year} Freight Pilot AI LLC</div>
      </div>
    </footer>
  );
}

/* ─────────────────────  STICKY BOTTOM CTA (mobile-first)  ───────────────────── */

function StickyCTA() {
  return (
    <div
      data-testid="tiktok-sticky-cta"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-md px-4 py-3 sm:hidden"
    >
      <Link
        to={PILOT_CTA}
        onClick={() => trackConversion("tiktok_sticky_cta")}
        data-testid="tiktok-sticky-cta-link"
        className="btn-primary w-full rounded-full px-5 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
      >
        Start 7-day free trial
        <ArrowRight size={16} weight="bold" />
      </Link>
    </div>
  );
}
